import type { Category, ConsentAction, ConsentChoice, LogPayload, OptionalCategory, ResolvedConfig } from '@cmp/shared';
import { BUILTIN_RULES, CATEGORIES, PLAIN_LABELS } from '@cmp/shared';
import { Blocker } from './blocker';
import { installDefaults, installUetDefault, update as gcmUpdate } from './gcm';
import { pickTexts } from './i18n';
import { cookieDomain, isValid, randomId, readState, writeState, clearState, type StoredConsent } from './state';
import { BannerUi, framePlaceholder } from './ui';

declare const __CMP_VERSION__: string;

export interface CmpApi {
  version: string;
  show(layer?: 1 | 2): void;
  hide(): void;
  getConsent(): ConsentChoice | null;
  getConsentId(): string | null;
  setConsent(choice: Partial<ConsentChoice>): void;
  acceptAll(): void;
  rejectAll(): void;
  withdraw(): void;
  /** Re-scan for manual text/plain tags after client-side navigation. */
  runScripts(): void;
  on(event: 'consent' | 'ready', cb: (detail: unknown) => void): () => void;
  /** Which rules this visitor got: the regime, their place and the rulebook release. */
  state(): { regime: string; place: string | null; rulebook: string | null };
}

declare global {
  interface Window {
    cmp?: CmpApi;
    __CMP_CONFIG?: ResolvedConfig;
  }
}

const ALL_ON: ConsentChoice = { necessary: true, functionality: true, analytics: true, marketing: true };
const ALL_OFF: ConsentChoice = { necessary: true, functionality: false, analytics: false, marketing: false };

function grantedList(c: ConsentChoice): Category[] {
  return CATEGORIES.filter((k) => c[k]);
}

function hasGpc(): boolean {
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

function emit(name: string, detail: unknown): void {
  document.dispatchEvent(new CustomEvent(`cmp:${name}`, { detail }));
}

function boot(): void {
  if (window.cmp) return;
  const script = document.currentScript as HTMLScriptElement | null;
  const scriptUrl = script?.src ? new URL(script.src, location.href) : null;
  const siteId = script?.dataset.site ?? scriptUrl?.searchParams.get('site') ?? null;
  const endpoint = script?.dataset.endpoint ?? scriptUrl?.origin ?? '';
  const noGcm = script?.dataset.gcm === 'off';

  // 1. Consent Mode defaults must precede gtag/GTM. Run before anything async.
  if (!noGcm) {
    installDefaults(500, true, false);
    installUetDefault();
  }

  // 2. Start gating immediately with built-in rules; site rules are appended once config arrives.
  //    Nothing is released until the stored choice has been validated against the live config,
  //    so a stale or tampered cookie can never let a tracker run before a real decision.
  const stored = readState();
  const blocker = new Blocker(BUILTIN_RULES);
  blocker.start();

  // 3. Load config (inline for dev, edge for production) and finish.
  const inline = window.__CMP_CONFIG;
  const load: Promise<ResolvedConfig> = inline
    ? Promise.resolve(inline)
    : fetch(`${endpoint}/cfg/${encodeURIComponent(siteId ?? '')}`, { credentials: 'omit' }).then((r) => {
        if (!r.ok) throw new Error(`cmp: config ${r.status}`);
        return r.json() as Promise<ResolvedConfig>;
      });

  // UI and the final decision wait for the DOM; blocking is already active, so nothing leaks meanwhile.
  const domReady: Promise<void> =
    document.readyState === 'loading' ? new Promise((r) => document.addEventListener('DOMContentLoaded', () => r())) : Promise.resolve();

  Promise.all([load, domReady]).then(([config]) => init(config, blocker, stored, endpoint, noGcm)).catch((err: unknown) => {
    // Fail closed: keep everything blocked, expose nothing. Log once for the site owner.
    console.warn('[cmp] could not load configuration; trackers stay blocked', err);
  });
}

function init(config: ResolvedConfig, blocker: Blocker, stored: StoredConsent | null, endpoint: string, noGcm: boolean): void {
  if (config.autoBlock) blocker.addRules(config.rules);
  else blocker.disableAutoBlock();
  const picked = pickTexts(config.texts, config.defaultLang);
  const lang = picked.lang;
  const texts = config.wording === 'plain' && PLAIN_LABELS[lang] ? { ...picked.texts, ...PLAIN_LABELS[lang] } : picked.texts;
  const domain = cookieDomain(config.domain, location.hostname);
  const gpc = hasGpc();
  const optout = config.regime === 'optout';
  // GPC is an opt-out where the rulebook says so. Configs from before the rulebook honour it everywhere.
  const gpcOptOut = gpc && config.gpc !== false;
  // California: a browser opt-out must be visibly confirmed, for as long as it stands. If the visitor
  // later chooses to allow tracking, the confirmation goes and the link reads "Your privacy choices".
  const gpcConfirmHere = optout && gpcOptOut && config.gpcConfirm === true;
  // A footer link marked data-cmp="settings" replaces the floating badge.
  const footerLink = !!document.querySelector('[data-cmp="settings"]');
  const showBadge = config.showBadge && !footerLink;
  // Where the law requires a visible privacy choices link: the site's own link if it has one, else a
  // plain link added to the page footer, and only if the page has no footer, the floating button.
  const needsLink = (config.link === true || gpcConfirmHere) && !showBadge;
  // Some places forbid a bar along the bottom of the page.
  const theme = config.noBar && config.theme.position === 'bottom' ? { ...config.theme, position: 'bottom-left' as const } : config.theme;

  let current: StoredConsent | null = isValid(stored, config.version, config.reconsentDays, config.refusalMemoryDays) ? stored : null;
  const honored = (): boolean => gpcConfirmHere && (!current || (!current.c.analytics && !current.c.marketing));
  const linkLabel = (): string => (honored() ? (texts.gpcHonored ?? 'Opt-out request honored') : texts.privacyChoices);

  const ui = new BannerUi(
    { texts, theme, privacyUrl: config.privacyUrl, cookieDeclarationUrl: config.cookieDeclarationUrl, showBadge, optout, branding: config.branding === true, gpcHonored: honored() },
    {
      onAcceptAll: () => decide(ALL_ON, 'accept_all'),
      onRejectAll: () => decide(ALL_OFF, 'reject_all'),
      onSave: (choice) => decide(choice, 'custom'),
    },
  );

  /** Keep the link and the settings' status line in step with the visitor's current choice. */
  const refreshHonored = () => {
    ui.setGpcHonored(honored());
    document.querySelectorAll('[data-cmp-added] a').forEach((a) => (a.textContent = linkLabel()));
  };
  if (needsLink) whenReady(() => placeLink(ui, linkLabel()));

  function apply(choice: ConsentChoice): void {
    blocker.release(grantedList(choice));
    if (!noGcm) gcmUpdate(choice, config);
    syncShopify(choice, optout && gpcOptOut);
    emit('consent', { choice, id: current?.id ?? null, regime: config.regime, place: config.place, rulebook: config.rulebook });
  }

  function persist(choice: ConsentChoice, action: ConsentAction, log: boolean): void {
    const state: StoredConsent = { id: current?.id ?? randomId(), v: config.version, ts: Date.now(), a: action, c: choice };
    current = state;
    const anyOptional = choice.analytics || choice.marketing || choice.functionality;
    writeState(state, anyOptional ? config.reconsentDays : config.refusalMemoryDays, domain);
    if (log) sendLog(endpoint, { site: config.siteId, id: state.id, ts: state.ts, action, categories: choice, version: config.version, lang, gpc });
  }

  function decide(choice: ConsentChoice, action: ConsentAction): void {
    persist(choice, action, true);
    apply(choice);
    ui.hide();
    refreshHonored();
  }

  // Decide what to do on this page load.
  if (current) {
    apply(current.c);
    ui.showBadgeOnly();
  } else if (config.regime === 'optin') {
    // Everything stays blocked until the visitor chooses. GPC users get analytics/marketing pre-set to off.
    ui.show(1, gpc ? ALL_OFF : undefined);
  } else if (optout) {
    // US-style: allowed by default, but a GPC signal is a legally binding opt-out.
    const choice: ConsentChoice = gpcOptOut ? { ...ALL_ON, analytics: false, marketing: false } : ALL_ON;
    persist(choice, gpcOptOut ? 'gpc' : 'implied', gpcOptOut);
    apply(choice);
    ui.showBadgeOnly();
    // California: say visibly that the signal was honoured, once per visitor (the link keeps saying it).
    if (honored()) ui.toast(texts.gpcHonored ?? 'Opt-out request honored');
    refreshHonored();
  } else if (config.regime === 'notice') {
    // UK statistical exception: first-party analytics and preferences run, marketing stays blocked,
    // a one-line notice offers Object. Nothing is stored until the visitor dismisses or objects,
    // so the notice repeats until they do.
    const running: ConsentChoice = { necessary: true, functionality: true, analytics: !gpc, marketing: false };
    apply(running);
    ui.showNotice({
      onDismiss: () => decide(running, 'implied'),
      onObject: () => decide({ ...running, analytics: false }, 'custom'),
    });
  } else {
    persist(ALL_ON, 'implied', false);
    apply(ALL_ON);
    ui.showBadgeOnly();
  }

  // Placeholder "Load content" buttons inside blocked iframes.
  const placeholder = framePlaceholder(texts, config.theme);
  window.addEventListener('message', (e: MessageEvent) => {
    const d = e.data as { cmp?: string; category?: OptionalCategory } | null;
    if (!d || d.cmp !== 'grant' || !d.category) return;
    const base = current?.c ?? ALL_OFF;
    decide({ ...base, [d.category]: true }, 'custom');
  });
  // Render placeholders for frames blocked before config arrived, and future ones.
  blocker.setPlaceholder(placeholder);

  const api: CmpApi = {
    version: __CMP_VERSION__,
    show: (layer = 1) => ui.show(layer, current?.c),
    hide: () => ui.hide(),
    getConsent: () => (current ? { ...current.c } : null),
    getConsentId: () => current?.id ?? null,
    setConsent: (partial) => decide({ ...(current?.c ?? ALL_OFF), ...partial, necessary: true }, 'custom'),
    acceptAll: () => decide(ALL_ON, 'accept_all'),
    rejectAll: () => decide(ALL_OFF, 'reject_all'),
    withdraw: () => {
      clearState(domain);
      current = null;
      decide(ALL_OFF, 'reject_all');
    },
    runScripts: () => blocker.sweep(),
    state: () => ({ regime: config.regime, place: config.place ?? null, rulebook: config.rulebook ?? null }),
    on: (event, cb) => {
      const handler = (e: Event) => cb((e as CustomEvent).detail);
      document.addEventListener(`cmp:${event}`, handler);
      return () => document.removeEventListener(`cmp:${event}`, handler);
    },
  };
  window.cmp = api;
  // Any element with data-cmp="settings" (a footer link, say) opens the settings layer.
  document.addEventListener('click', (e) => {
    const el = (e.target as Element | null)?.closest?.('[data-cmp="settings"]');
    if (!el) return;
    e.preventDefault();
    api.show(2);
  });
  emit('ready', { regime: config.regime, country: config.country, place: config.place, rulebook: config.rulebook, lang });
}

/**
 * Shopify: pass the choice to Shopify's Customer Privacy API, so its own pixels and checkout
 * (where no other script may run) follow the same decision. Does nothing on other platforms.
 */
type ShopifyWindow = Window & {
  Shopify?: {
    loadFeatures?: (f: { name: string; version: string }[], cb: (err?: unknown) => void) => void;
    customerPrivacy?: { setTrackingConsent: (c: Record<string, boolean>, cb?: () => void) => void };
  };
};
function syncShopify(choice: ConsentChoice, saleOptOut: boolean): void {
  const w = window as ShopifyWindow;
  if (!w.Shopify) return;
  const send = () => {
    try {
      w.Shopify?.customerPrivacy?.setTrackingConsent({ analytics: choice.analytics, marketing: choice.marketing, preferences: choice.functionality, sale_of_data: choice.marketing && !saleOptOut }, () => {});
    } catch {
      /* never let the store's own API break the page */
    }
  };
  if (w.Shopify.customerPrivacy) return send();
  try {
    w.Shopify.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
      if (!err) send();
    });
  } catch {
    /* not available on this storefront */
  }
}

function sendLog(endpoint: string, payload: LogPayload): void {
  if (!endpoint) return;
  const body = JSON.stringify(payload);
  const url = `${endpoint}/log`;
  // text/plain keeps this a CORS "simple request": no preflight, and sendBeacon accepts it.
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }))) return;
  } catch {
    /* fall through */
  }
  void fetch(url, { method: 'POST', body, headers: { 'content-type': 'text/plain;charset=UTF-8' }, keepalive: true, credentials: 'omit' }).catch(() => {});
}

boot();

function whenReady(fn: () => void): void {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
  else fn();
}

/**
 * The privacy choices link, as unobtrusive as the law allows: nothing if the site has its own
 * data-cmp="settings" link, a plain link at the end of the page footer (in the footer's own style)
 * if there is one, and the floating button only when the page has no footer at all.
 */
function placeLink(ui: BannerUi, label: string): void {
  const lastFooter = () => {
    const all = document.querySelectorAll('footer, [role="contentinfo"]');
    return all[all.length - 1] ?? null;
  };
  const place = (): boolean => {
    if (document.querySelector('[data-cmp="settings"]')) return true;
    const footer = lastFooter();
    if (!footer) return false;
    // Step into a footer's lone wrapper (<footer><div class="wrap">…) so the link lines up with the
    // footer's own text instead of sitting outside its padding. Stop at text and list elements.
    let target: Element = footer;
    while (target.children.length === 1 && !/^(A|SPAN|B|STRONG|EM|SMALL|IMG|SVG|BUTTON|P|UL|OL|NAV|FORM)$/.test(target.children[0]!.tagName)) target = target.children[0]!;
    const p = document.createElement('p');
    p.setAttribute('data-cmp-added', '');
    p.style.cssText = 'margin:.75em 0 0;font:inherit;color:inherit';
    const a = document.createElement('a');
    a.href = '#';
    a.setAttribute('data-cmp', 'settings');
    a.style.cssText = 'color:inherit;text-decoration:underline';
    a.textContent = label;
    p.appendChild(a);
    target.appendChild(p);
    return true;
  };
  if (!place()) {
    ui.enableBadge();
    return;
  }
  // Frameworks that re-render the footer after load can drop the link; put it back once, later.
  setTimeout(() => {
    if (!place()) ui.enableBadge();
  }, 4000);
}
